import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ApiService, ApiError } from './services/api.service';
import { CurrentUserService } from './services/current-user-service';

export interface LoginResponse {
  tenantId: string;
  token: string;
}

export interface User {
  username: string;
  email: string;
  unique_name: string;
  nbf: Date;
  exp: Date;
  iat: Date
}


export interface AuthError {
  message: string;
  code?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router: Router = inject(Router);
  private api: ApiService = inject(ApiService);
  private currentUserService: CurrentUserService = inject(CurrentUserService)

  private currentUserSubject = this.currentUserService.currentUserSubject;
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<AuthError | null>(null);

  public currentUser$ = this.currentUserSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();


  public currentUser: WritableSignal<User | undefined> = signal(undefined)

  constructor() {
    // Check if user is already logged in (from localStorage)
    // Only access localStorage in browser environment
    if (typeof window !== 'undefined' && window.localStorage && window.sessionStorage) {
      const savedUser = window.sessionStorage.getItem('currentUser') || window.localStorage.getItem('currentUser')
      if (savedUser) {
        try {
          this.currentUserSubject.next(this.decodeToken(savedUser));
        } catch (error) {
          console.error('Error parsing saved user:', error);
          this.clearStoredUser();
        }
      }
    }
  }

  login(email: string, password: string, rememberMe: boolean): Observable<LoginResponse> {
    this.setLoading(true);
    this.clearError();

    return this.api
      .post<LoginResponse>('auth/login', JSON.stringify({ email, password }))
      .pipe(
        tap(() => this.setLoading(false)),
        map((res) => {
          // Successful login
          const user = this.decodeToken(res?.token);
          if (typeof window !== 'undefined' && window.localStorage) {
            if (rememberMe) {
              localStorage.setItem('currentUser', res.token);
            } else {
              sessionStorage.setItem('currentUser', res.token);
            }
          }

          this.setLoading(false);
          this.currentUserSubject.next(user);
          return res;
        }),
        catchError((error: ApiError) => {
          this.setLoading(false);
          const authError: AuthError = {
            message: error.message || 'Wystąpił błąd podczas logowania',
            code: error.code,
          };
          this.setError(authError);
          return throwError(() => authError);
        })
      );
  }

  decodeToken(token: string): User {
    if (!token) {
      throw new Error('Token is missing ');
    }

    try {
      // JWT token format: header.payload.signature
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      // Decode payload (second part)
      const payload = tokenParts[1];

      // Base64URL decode
      // Replace URL-safe characters and add padding if needed
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

      // Decode and parse JSON
      const decodedPayload = JSON.parse(atob(padded));

      // Map decoded payload to User interface
      const user: User = {
        username: decodedPayload.username || decodedPayload.sub || decodedPayload.name || '',
        email: decodedPayload.email || decodedPayload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '',
        unique_name: decodedPayload.unique_name || decodedPayload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || decodedPayload.sub || '',
        nbf: decodedPayload.nbf ? new Date(decodedPayload.nbf * 1000) : new Date(),
        exp: decodedPayload.exp ? new Date(decodedPayload.exp * 1000) : new Date(),
        iat: decodedPayload.iat ? new Date(decodedPayload.iat * 1000) : new Date(),
      };
      return user;
    } catch (error) {
      console.error('Error decoding token:', error);
      throw new Error('Failed to decode token');
    }
  }

  encodeToken(user: User): string {
    if (!user) {
      throw new Error('User is missing');
    }

    try {
      // JWT header
      const header = {
        alg: 'none',
        typ: 'JWT'
      };

      // JWT payload
      const payload = {
        username: user.username,
        email: user.email,
        unique_name: user.unique_name,
        nbf: Math.floor(new Date(user.nbf).getTime() / 1000),
        exp: Math.floor(new Date(user.exp).getTime() / 1000),
        iat: Math.floor(new Date(user.iat).getTime() / 1000)
      };

      // Base64URL encode helper
      const base64UrlEncode = (obj: object): string => {
        return btoa(JSON.stringify(obj))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      };

      const encodedHeader = base64UrlEncode(header);
      const encodedPayload = base64UrlEncode(payload);

      // No signature (alg: none)
      const signature = '';

      return `${encodedHeader}.${encodedPayload}.${signature}`;
    } catch (error) {
      console.error('Error encoding token:', error);
      throw new Error('Failed to encode token');
    }
  }

  mockLogin(
    email: string,
    password: string,
    rememberMe = false
  ): Promise<boolean> {
    return new Promise((resolve) => {
      this.setLoading(true);
      // Simulate API call with realistic timing
      setTimeout(() => {
        // Mock authentication - in real app, this would be an API call
        if (email && password) {
          // Simulate different error scenarios
          if (email === 'error@example.com') {
            this.setError({
              message: 'Invalid credentials. Please try again.',
              code: 'INVALID_CREDENTIALS',
            });
            this.setLoading(false);
            resolve(false);
            return;
          }

          if (email === 'locked@example.com') {
            this.setError({
              message: 'Account is locked. Please contact support.',
              code: 'ACCOUNT_LOCKED',
            });
            this.setLoading(false);
            resolve(false);
            return;
          }

          if (email === 'network@example.com') {
            this.setError({
              message: 'Network error. Please check your connection.',
              code: 'NETWORK_ERROR',
            });
            this.setLoading(false);
            resolve(false);
            return;
          }

          // Successful login
          const now = new Date();
          const user: User = {
            username: email.split('@')[0],
            email: email,
            unique_name: email,
            nbf: now,
            exp: new Date(now.getTime() + 24 * 60 * 60 * 1000), // 24 hours from now
            iat: now,
          };

          this.currentUserSubject.next(user);
          const token = this.encodeToken(user)

          if (typeof window !== 'undefined' && window.localStorage) {
            if (rememberMe) {
              localStorage.setItem('currentUser', token);
            } else {
              sessionStorage.setItem('currentUser', token);
            }
          }

          this.setLoading(false);
          resolve(true);
        } else {
          this.setError({
            message: 'Please provide valid credentials.',
            code: 'MISSING_CREDENTIALS',
          });
          this.setLoading(false);
          resolve(false);
        }
      }, 1500); // Simulate network delay
    });
  }

  async logout(): Promise<void> {
    this.setLoading(true);
    await this.currentUserService.logout(true)
    this.setLoading(false);
  }

  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  // Clear stored user data
  private clearStoredUser(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('currentUser');
      sessionStorage.removeItem('currentUser');
    }
  }

  // Set loading state
  private setLoading(loading: boolean): void {
    this.isLoadingSubject.next(loading);
  }

  // Set error state
  private setError(error: AuthError): void {
    this.errorSubject.next(error);
  }

  // Clear error state
  private clearError(): void {
    this.errorSubject.next(null);
  }

  // Public method to clear error
  clearErrorState(): void {
    this.clearError();
  }
}
